import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackUserEvent } from '../analytics';

export const GUEST_PAY_EVENT_IDS = {
  LANDING_CONTINUE: '9319', // User clicks Continue on landing page with invoice details
  LANDING_HELP_MODAL_OPEN: '9320', // User opens "Where are my invoice numbers?" modal
  LANDING_SIGN_IN: '9321', // User clicks "Sign in to pay" button
  SELECT_INVOICE: '9322', // User selects/deselects invoices for payment
  SELECT_INVOICE_CONTINUE: '9323', // User clicks Continue to payment details
  SELECT_INVOICE_CANCEL: '9324', // User clicks Back/Cancel button
  SELECT_INVOICE_HELP_MODAL_OPEN: '9325', // User opens invoice inquiry modal
  SELECT_INVOICE_SUBMIT_HELP: '9326', // User submits support request
  PAYMENT_METHOD_SELECT_ACH: '9327', // User selects ACH payment method
  PAYMENT_METHOD_SELECT_CREDIT_CARD: '9328', // User selects Credit Card payment method
  PAYMENT_METHOD_CONTINUE: '9329', // User clicks Continue to add payment method
  PAYMENT_METHOD_BACK: '9330', // User clicks Back button
  SUBMIT_ACCEPT_TERMS: '9331', // User accepts terms and conditions
  SUBMIT_PAYMENT: '9332', // User clicks Submit Payment button
  SUBMIT_BACK: '9333', // User clicks Back button
  CONFIRMATION_DOWNLOAD_PDF: '9334', // User downloads payment summary PDF
  CONFIRMATION_PAY_ANOTHER: '9335', // User clicks "Pay another invoice"
} as const;

// https://dataportal.zillowgroup.net/event-registry/9319
export const emitGuestPayLandingContinueEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.LANDING_CONTINUE,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_landing',
      trigger_source_nm: 'button_to_continue',
      trigger_object_nm: 'invoice_authentication_form',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_landing_continue',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9320
export const emitGuestPayLandingHelpModalOpenEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.LANDING_HELP_MODAL_OPEN,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_landing',
      trigger_source_nm: 'text_button_to_help',
      trigger_object_nm: 'invoice_help_modal',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_landing_help_modal_open',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9321
export const emitGuestPayLandingSignInEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.LANDING_SIGN_IN,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_landing',
      trigger_source_nm: 'button_to_sign_in',
      trigger_object_nm: 'authentication_widget',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_landing_sign_in',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9322
export const emitGuestPaySelectInvoiceEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.SELECT_INVOICE,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_invoice_selection',
      trigger_source_nm: 'checkbox_to_select_invoice',
      trigger_object_nm: 'invoice_table',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_select_invoice',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9323
export const emitGuestPaySelectInvoiceContinueEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.SELECT_INVOICE_CONTINUE,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_invoice_selection',
      trigger_source_nm: 'button_to_continue',
      trigger_object_nm: 'invoice_selection_page',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_invoice_continue',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9324
export const emitGuestPaySelectInvoiceCancelEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.SELECT_INVOICE_CANCEL,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_invoice_selection',
      trigger_source_nm: 'button_to_cancel',
      trigger_object_nm: 'cancel_modal',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_invoice_cancel',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9325
export const emitGuestPaySelectInvoiceHelpModalOpenEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.SELECT_INVOICE_HELP_MODAL_OPEN,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_invoice_selection',
      trigger_source_nm: 'text_button_to_contact_us',
      trigger_object_nm: 'invoice_help_modal',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_invoice_help_modal_open',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9326
export const emitGuestPaySelectInvoiceSubmitHelpEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.SELECT_INVOICE_SUBMIT_HELP,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_invoice_selection',
      trigger_source_nm: 'button_to_send_help_request',
      trigger_object_nm: 'invoice_help_modal',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_invoice_submit_help',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9327
export const emitGuestPayPaymentMethodSelectACHEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.PAYMENT_METHOD_SELECT_ACH,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_payment_details',
      trigger_source_nm: 'radio_to_select_ach',
      trigger_object_nm: 'payment_method_form',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_payment_method_select_ach',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9328
export const emitGuestPayPaymentMethodSelectCreditCardEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.PAYMENT_METHOD_SELECT_CREDIT_CARD,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_payment_details',
      trigger_source_nm: 'radio_to_select_credit_card',
      trigger_object_nm: 'payment_method_form',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_payment_method_select_credit_card',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9329
export const emitGuestPayPaymentMethodContinueEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.PAYMENT_METHOD_CONTINUE,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_payment_details',
      trigger_source_nm: 'button_to_continue',
      trigger_object_nm: 'payment_method_form',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_payment_method_continue',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9330
export const emitGuestPayPaymentMethodBackEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.PAYMENT_METHOD_BACK,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_payment_details',
      trigger_source_nm: 'button_to_back',
      trigger_object_nm: 'payment_details_page',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_payment_method_back',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9331
export const emitGuestPaySubmitAcceptTermsEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.SUBMIT_ACCEPT_TERMS,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_submit_payment',
      trigger_source_nm: 'checkbox_to_accept_terms',
      trigger_object_nm: 'terms_and_conditions',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_submit_accept_terms',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9332
export const emitGuestPaySubmitPaymentEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.SUBMIT_PAYMENT,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_submit_payment',
      trigger_source_nm: 'button_to_submit_payment',
      trigger_object_nm: 'submit_payment_page',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_submit_payment',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9333
export const emitGuestPaySubmitBackEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.SUBMIT_BACK,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_submit_payment',
      trigger_source_nm: 'button_to_back',
      trigger_object_nm: 'submit_payment_page',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_submit_back',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9334
export const emitGuestPayConfirmationDownloadPDFEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.CONFIRMATION_DOWNLOAD_PDF,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_confirmation',
      trigger_source_nm: 'button_to_download_summary',
      trigger_object_nm: 'payment_confirmation_page',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_confirmation_download_pdf',
      topic_tag_txt: ['guest_pay', 'payment', 'receipt', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9335
export const emitGuestPayConfirmationPayAnotherEvent = ({ user }: { user: ZrmClickstreamUser }) =>
  trackUserEvent({
    envelope: {
      event_type_id: GUEST_PAY_EVENT_IDS.CONFIRMATION_PAY_ANOTHER,
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'guest_pay_confirmation',
      trigger_source_nm: 'button_to_pay_another_invoice',
      trigger_object_nm: 'payment_confirmation_page',
    },
    semantic: {
      semantic_event_nm: 'guest_pay_confirmation_pay_another',
      topic_tag_txt: ['guest_pay', 'zrm_enterprise'],
    },
    ...getZrmUserInfo(user),
  });
